import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, MapPin, PenLine } from "lucide-react";
import { cn } from "@/lib/utils";
import { citiesFor } from "@/data/canada_cities";

// Searchable city picker. A plain text input that filters the full city list
// for the given province, with keyboard (up/down/enter/escape) and mouse
// support. Free text is always accepted, and a final "Other city" option makes
// that explicit, so nobody is ever blocked by a missing town.
// Styled to match InputField/SelectField. No dependencies.
export default function CityCombobox({
  label = "City",
  provinceCode,
  value = "",
  onChange,
  error,
  placeholder = "Type to search cities",
  anyLabel, // optional first option, e.g. "Any city" -> onChange("")
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(null); // null = show the committed value
  const [hi, setHi] = useState(0);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const cities = useMemo(() => citiesFor(provinceCode), [provinceCode]);
  const q = (query ?? "").trim().toLowerCase();
  const filtered = q ? cities.filter((c) => c.toLowerCase().includes(q)) : cities;

  const options = useMemo(() => {
    const list = [];
    if (anyLabel) list.push({ kind: "any", label: anyLabel });
    filtered.forEach((c) => list.push({ kind: "city", label: c }));
    const typed = (query ?? "").trim();
    list.push({
      kind: "other",
      label: typed && !filtered.some((c) => c.toLowerCase() === typed.toLowerCase())
        ? `Other city: use "${typed}"`
        : "Other city",
    });
    return list;
  }, [anyLabel, filtered, query]);

  // Keep the highlight in range whenever the option list changes.
  useEffect(() => {
    setHi((h) => Math.min(h, Math.max(0, options.length - 1)));
  }, [options.length]);

  // Scroll the highlighted row into view while arrowing through the list.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[hi];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [hi, open]);

  function commit(next) {
    onChange?.(next);
    setQuery(null);
    setOpen(false);
  }

  function pick(opt) {
    if (opt.kind === "any") return commit("");
    if (opt.kind === "city") return commit(opt.label);
    // "Other city": keep whatever was typed, or hand focus back for free entry.
    const typed = (query ?? "").trim();
    if (typed) return commit(typed);
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  }

  function onKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHi((h) => (h + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHi((h) => (h - 1 + options.length) % options.length);
    } else if (e.key === "Enter") {
      if (open && options[hi]) {
        e.preventDefault();
        pick(options[hi]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery(null);
    }
  }

  function onBlur() {
    // Option rows preventDefault on mousedown, so a click never reaches here.
    setOpen(false);
    if (query !== null) {
      const typed = query.trim();
      // Snap to the exact list spelling when it matches, otherwise keep as typed.
      const match = cities.find((c) => c.toLowerCase() === typed.toLowerCase());
      onChange?.(match || typed);
      setQuery(null);
    }
  }

  const shown = query !== null ? query : value || "";
  const hint = anyLabel && !value ? anyLabel : placeholder;

  return (
    <label className={cn("relative block text-left", className)}>
      {label ? <span className="mb-1.5 block text-sm font-semibold text-brand-ink">{label}</span> : null}
      <span className="relative block">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          value={shown}
          placeholder={hint}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHi(anyLabel ? 1 : 0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          className={cn(
            "w-full rounded-xl border bg-white px-4 py-2.5 pr-10 text-sm text-brand-ink outline-none transition placeholder:text-slate-400 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20",
            error ? "border-amber-500" : "border-slate-200"
          )}
        />
        <ChevronDown
          size={16}
          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400"
        />
      </span>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute left-0 right-0 z-30 mt-1.5 max-h-60 overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
        >
          {!cities.length && (
            <li className="px-4 py-2 text-xs text-slate-400">
              Pick a province to see city suggestions, or type any city below.
            </li>
          )}
          {options.map((opt, i) => (
            <li
              key={`${opt.kind}-${opt.label}`}
              role="option"
              aria-selected={i === hi}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHi(i)}
              onClick={() => pick(opt)}
              className={cn(
                "flex cursor-pointer items-center gap-2 px-4 py-2 text-sm",
                i === hi ? "bg-brand-tint text-brand-ink" : "text-brand-ink",
                opt.kind === "other" && "border-t border-slate-100 text-brand-secondary"
              )}
            >
              {opt.kind === "other" ? (
                <PenLine size={14} className="shrink-0 text-slate-400" />
              ) : (
                <MapPin size={14} className="shrink-0 text-brand-primary/70" />
              )}
              <span className="truncate">{opt.label}</span>
            </li>
          ))}
        </ul>
      )}

      {error ? <span className="mt-1 block text-xs font-semibold text-amber-600">{error}</span> : null}
    </label>
  );
}
